'use client'

import { type AccountNavigation, type MainNavigation } from 'cms-types'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useMemo, useRef } from 'react'
import { addAlert, Navigation } from 'ui'
import { LazyMotion, m, useScroll, useTransform } from 'ui/helpers/motion'

import { CLUB_PAGE_URL, SET_CREATOR_PAGE_URL } from '@/helpers/club'
import { logger } from '@/helpers/logger'
import { useCanSeeClub } from '@/hooks/useCanSeeClub'
import { useCanSeeWunschset } from '@/hooks/useCanSeeWunschset'
import { useSSR } from '@/hooks/useSSR'
import { useCart } from '@/store/cartStore'
import { useCustomer } from '@/store/customerStore'

import { pageToUrl, resolveRelation } from '../helpers'

const motionFeatures = () => import('ui/helpers/motionFeatures').then(res => res.default)

type MainNavigationProps = MainNavigation & {
  accountNavigation?: AccountNavigation
}

export default function MainNavigation(props: MainNavigationProps) {
  const cart = useCart()
  const { isClient } = useSSR()
  const pathname = usePathname()
  const router = useRouter()
  const headerRef = useRef<HTMLHeadElement>(null)
  const scrollOffset = useRef(0)
  const customer = useCustomer()
  const canSeeClub = useCanSeeClub()
  const canSeeWunschset = useCanSeeWunschset()

  const shoppingBasketItemsCount = useMemo(() => {
    if (!cart.data) return

    return cart.data.lines.reduce((accum, item) => accum + item.quantity, 0)
  }, [cart.data])

  const mappedMenuItems = useMemo(() => {
    let activeLinkIndex = null
    let maximumMatches = 0

    const splittedPathname = pathname.split('/').slice(1)
    // Blog posts live under /post/<slug>, but should highlight the "hautwissen" nav item.
    const splittedCurrentPathname =
      splittedPathname[0] === 'post'
        ? ['hautwissen', ...splittedPathname.slice(1)]
        : splittedPathname

    const menuEntries = (props.menu ?? [])
      .map(({ title, page, id }) => ({ title, url: pageToUrl(page) ?? '', id }))
      // Hide the Wunschset entry from ineligible customers during the release phase.
      .filter(item => canSeeWunschset || item.url !== SET_CREATOR_PAGE_URL)

    const mappedItem = menuEntries.map(({ title, url: linkUrl, id }, linkIndex) => {
      const splittedLink = linkUrl.split('/').slice(1)

      let pathMatches = 0

      splittedLink.forEach((path, pathIndex) => {
        if (path === splittedCurrentPathname[pathIndex]) {
          pathMatches++
        }
      })

      if (pathMatches > maximumMatches) {
        activeLinkIndex = linkIndex
        maximumMatches = pathMatches
      }

      return {
        title,
        url: linkUrl,
        id,
        isActive: false,
      }
    })

    if (activeLinkIndex != null) {
      mappedItem[activeLinkIndex].isActive = true
    }

    return mappedItem
  }, [pathname, props.menu, canSeeWunschset])

  const onSearchSubmit = useCallback(
    (query: string) => {
      if (!isClient) return

      router.push(`/suche/${encodeURIComponent(query)}`)
    },
    [isClient, router],
  )

  const logo = resolveRelation(props.logo?.image)

  const goToLogout = useCallback(async () => {
    try {
      await customer.logout()
      if (pathname === '/') {
        router.refresh()
      } else {
        router.push('/')
      }
    } catch (error) {
      logger.error('MainNavigation > goToLogout', error)
      addAlert.error('Logout fehlgeschlagen')
    }
  }, [customer, pathname, router])

  const goToLogin = () => {
    router.push('/account/login')
  }

  const goToProfile = () => {
    router.push('/account')
  }

  const goToDefaultAccountPage = useCallback(() => {
    const defaultAccountPageUrl =
      pageToUrl(props.accountNavigation?.defaultAccountPage) ?? '/account'
    router.push(defaultAccountPageUrl)
  }, [props.accountNavigation?.defaultAccountPage, router])

  const accountNavItems = useMemo(() => {
    const menuItems = (props.accountNavigation?.menu ?? [])
      .map(({ icon, title, page, id }) => ({
        id,
        title,
        icon,
        url: pageToUrl(page) ?? '',
      }))
      // Hide the club entry from non-eligible customers during the release phase.
      .filter(
        item =>
          (canSeeClub || item.url !== CLUB_PAGE_URL) &&
          (canSeeWunschset || item.url !== SET_CREATOR_PAGE_URL),
      )

    return [
      {
        id: 'profile',
        title: props.login?.typography?.profile ?? 'Profile',
        icon: 'MdPerson',
        url: '/account',
      },
      ...menuItems,
      {
        id: 'logout',
        title: props.login?.typography?.logout ?? 'Logout',
        icon: 'MdOutlineLogout',
        onClick: goToLogout,
      },
    ]
  }, [
    props.accountNavigation?.menu,
    props.login?.typography?.profile,
    props.login?.typography?.logout,
    goToLogout,
    canSeeClub,
    canSeeWunschset,
  ])

  const { scrollY } = useScroll()

  const transform = useTransform(scrollY, scrollPosition => {
    if (!headerRef.current || scrollPosition < headerRef.current.clientHeight / 2)
      return 'translateY(0)'

    const translate = scrollPosition > scrollOffset.current ? 'translateY(-100%)' : 'translateY(0)'
    scrollOffset.current = scrollPosition

    return translate
  })

  return (
    <LazyMotion features={motionFeatures} strict>
      <m.header
        ref={headerRef}
        className='sticky top-0 z-50 transition-transform duration-300 ease-in-out'
        style={{ transform }}
      >
        <Navigation
          onShoppingBasketIconClick={() => cart.setIsOpen(true)}
          shoppingBasketItemsCount={shoppingBasketItemsCount}
          onSearchSubmit={onSearchSubmit}
          logo={logo}
          logoUrl={pageToUrl(props.logo?.page) ?? ''}
          searchEnabled={props.search?.enabled}
          searchFieldPlaceholder={props.search?.placeholder}
          searchButtonLabel={props.search.searchButtonLabel}
          closeSearchButtonLabel={props.search.closeButtonLabel}
          searchSuggestion={
            props.search
              ? {
                  title: props.search.suggestionList.suggestionListTitle,
                  links:
                    props.search.suggestionList.links?.map(({ title, id }) => ({
                      title,
                      id,
                    })) ?? [],
                }
              : undefined
          }
          shoppingBasketEnabled={props.shoppingBasket?.enabled}
          loginEnabled={props.login.enabled}
          loginTypography={props.login.typography}
          isLogin={customer.token != null}
          firstName={customer.customer?.firstName}
          accountNavTitle={
            props.accountNavigation?.title ?? props.login?.typography?.profile ?? 'Account'
          }
          accountNavItems={accountNavItems}
          menuItems={mappedMenuItems}
          burgerMenuAdditionalListItems={
            props.burgerMenuAdditionalList?.map(({ title, page, id }) => ({
              title,
              url: pageToUrl(page) ?? '',
              id,
            })) ?? []
          }
          goToLogout={goToLogout}
          goToLogin={goToLogin}
          goToProfile={goToProfile}
          goToDefaultAccountPage={goToDefaultAccountPage}
        />
      </m.header>
    </LazyMotion>
  )
}
